// public/js/modules/map/accuracy.js
// Draws GPS position-accuracy as two overlaid translucent areas:
//   1) a "tube" — a buffer ribbon along the path whose half-width equals the
//      accuracy radius at each fix (so the tube is exactly the circle diameter
//      wide where they meet), in a lighter, more transparent colour, and
//   2) one circle per fix, in a slightly stronger colour. The circles also fill
//      the round joints between tube segments on turns.
// Each set lives in a single fill layer, so self-overlap keeps a uniform
// opacity (no compounding/darkening). Can be toggled on/off globally.

const ACC_SOURCE = 'asset-accuracy-src';
const ACC_FILL = 'asset-accuracy-fill';
const ACC_LINE = 'asset-accuracy-line';
const TUBE_SOURCE = 'asset-accuracy-tube-src';
const TUBE_FILL = 'asset-accuracy-tube-fill';

// Circles: stronger blue. Tube: different hue + more transparent.
const CIRCLE_COLOR = '#3b82f6';
const CIRCLE_OPACITY = 0.13;
const TUBE_COLOR = '#a78bfa';
const TUBE_OPACITY = 0.07;

const EARTH_R = 6378137;    // metres
const DEG_PER_M = 111320;   // metres per degree (lat)

function safeLS(key) { try { return localStorage.getItem(key); } catch (e) { return null; } }

let enabled = safeLS('map.accuracy') !== 'off'; // default ON
let last = null; // { map, circles, path } — last requested draw, for re-enable

export function isAccuracyEnabled() { return enabled; }

export function setAccuracyEnabled(map, val) {
  enabled = !!val;
  try { localStorage.setItem('map.accuracy', enabled ? 'on' : 'off'); } catch (e) {}
  if (!enabled) { clearAccuracy(map); }
  else if (last) { _render(last.map || map, last.circles, last.path); }
}

// Polygon ring approximating a circle of `radiusM` metres around lon/lat.
function circleRing(lon, lat, radiusM, steps = 48) {
  const dLat = (radiusM / EARTH_R) * (180 / Math.PI);
  const dLon = (radiusM / (EARTH_R * Math.cos((lat * Math.PI) / 180))) * (180 / Math.PI);
  const ring = [];
  for (let i = 0; i <= steps; i++) {
    const a = (2 * Math.PI * i) / steps;
    ring.push([lon + dLon * Math.cos(a), lat + dLat * Math.sin(a)]);
  }
  return ring;
}

// Tube as one trapezoid per segment. Each segment is offset by the fix radius
// perpendicular to THAT segment, so its half-width is exactly the accuracy at
// each endpoint → the segment edge is tangent to the endpoint circle and the
// tube is the circle's full diameter wide where they meet. Joints are covered
// by the per-fix circles.
function buildTubeFeatures(path) {
  const pts = (path || []).filter(p =>
    Number.isFinite(p.lon) && Number.isFinite(p.lat) && Number.isFinite(p.radius) && p.radius > 0);
  if (pts.length < 2) return [];

  const lat0 = pts[0].lat, lon0 = pts[0].lon;
  const cosLat0 = Math.cos((lat0 * Math.PI) / 180) || 1e-6;
  const toLngLat = ([x, y]) => [lon0 + x / (cosLat0 * DEG_PER_M), lat0 + y / DEG_PER_M];

  const xy = pts.map(p => ({
    x: (p.lon - lon0) * cosLat0 * DEG_PER_M,
    y: (p.lat - lat0) * DEG_PER_M,
    r: p.radius,
  }));

  const features = [];
  for (let i = 0; i < xy.length - 1; i++) {
    const a = xy[i], b = xy[i + 1];
    const dx = b.x - a.x, dy = b.y - a.y;
    const len = Math.hypot(dx, dy) || 1e-6;
    const nx = -dy / len, ny = dx / len; // left normal of this segment
    const ring = [
      [a.x + nx * a.r, a.y + ny * a.r],
      [b.x + nx * b.r, b.y + ny * b.r],
      [b.x - nx * b.r, b.y - ny * b.r],
      [a.x - nx * a.r, a.y - ny * a.r],
    ].map(toLngLat);
    ring.push(ring[0]);
    features.push({ type: 'Feature', geometry: { type: 'Polygon', coordinates: [ring] }, properties: {} });
  }
  return features;
}

export function clearAccuracy(map) {
  if (!map) return;
  [ACC_LINE, ACC_FILL, TUBE_FILL].forEach(id => {
    try { if (map.getLayer(id)) map.removeLayer(id); } catch (e) {}
  });
  [ACC_SOURCE, TUBE_SOURCE].forEach(id => {
    try { if (map.getSource(id)) map.removeSource(id); } catch (e) {}
  });
}

/**
 * Draw accuracy areas (no-op when toggled off, but remembers the request so it
 * can be redrawn when re-enabled). Replaces any previously drawn areas.
 * @param {object} map
 * @param {object} opts
 * @param {Array<{lon,lat,radius}>} opts.circles  one circle per fix (metres)
 * @param {Array<{lon,lat,radius}>} [opts.path]    ordered path → tube
 */
export function drawAccuracy(map, { circles = [], path = null } = {}) {
  last = { map, circles, path };
  if (!map) return;
  if (!enabled) { clearAccuracy(map); return; }
  _render(map, circles, path);
}

function _render(map, circles, path) {
  if (!map) return;
  clearAccuracy(map);

  // 1) Tube (under the circles) — lighter, more transparent.
  const tubeFeatures = path ? buildTubeFeatures(path) : [];
  if (tubeFeatures.length) {
    map.addSource(TUBE_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features: tubeFeatures } });
    map.addLayer({
      id: TUBE_FILL, type: 'fill', source: TUBE_SOURCE,
      paint: { 'fill-color': TUBE_COLOR, 'fill-opacity': TUBE_OPACITY },
    });
  }

  // 2) Per-fix circles (on top).
  const features = (circles || [])
    .filter(c => Number.isFinite(c.lon) && Number.isFinite(c.lat) && Number.isFinite(c.radius) && c.radius > 0)
    .map(c => ({
      type: 'Feature',
      geometry: { type: 'Polygon', coordinates: [circleRing(c.lon, c.lat, c.radius)] },
      properties: {},
    }));

  if (features.length) {
    map.addSource(ACC_SOURCE, { type: 'geojson', data: { type: 'FeatureCollection', features } });
    map.addLayer({
      id: ACC_FILL, type: 'fill', source: ACC_SOURCE,
      paint: { 'fill-color': CIRCLE_COLOR, 'fill-opacity': CIRCLE_OPACITY },
    });
    map.addLayer({
      id: ACC_LINE, type: 'line', source: ACC_SOURCE,
      paint: { 'line-color': CIRCLE_COLOR, 'line-opacity': 0.45, 'line-width': 1 },
    });
  }
}
